/* eslint-disable no-unused-expressions */
import lodash from 'lodash'
import { getABTProvider } from '@/plugins/abt/statsig'

import LOGGING_EVENTS from '@/constants/logging-events'
import { isString, isNumber, isObject } from '@/utils/validate'
import { maskEmail, maskPhoneNumber } from '@/utils/logger'
const abtProvider = getABTProvider()
const APP_NAME = 'partner_portal_ui'
const DEBUG_LOGGER_FEATURE_FLAG = 'newrelic_debug_logs_enabled'

const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  TRACE: 'trace',
}

let clientLogger = null
window.loggerDataLayer = [] // Define data layer for logging. We will use this to cache event before NewRelic is initialized

const parseExtra = (extra) => {
  try {
    if (!extra) {
      return {}
    }

    if (isString(extra) || isNumber(extra)) {
      return { extra }
    }

    if (isObject(extra) && isObject(extra?.data ?? null)) {
      return extra?.data ?? {}
    }

    if (isObject(extra)) {
      return extra
    }

    return { extra: JSON.stringify(extra) }
  } catch (err) {
    return {}
  }
}

const identify = ({ email, phoneNumber }) => {
  window.loggerIdentify = {
    email: maskEmail(email),
    phoneNumber: maskPhoneNumber(phoneNumber),
  }
}

const setDefaultAttributes = (window) => {
  const defaultAttributes = getDefaultAttributes(window)
  setCustomAttributes(defaultAttributes)
}

const setCustomAttributes = (customAttributes = {}) => {
  if (!clientLogger) {
    return
  }
  Object.keys(customAttributes).forEach((key) => {
    clientLogger.setCustomAttribute(key, customAttributes[key])
  })
}

const getDefaultAttributes = (window, extra) => {
  const queryString = window.location?.search || ''
  const fullPath = window.location?.href || ''
  const { email = 'Unknown', phoneNumber = 'Unknown' } =
    window.loggerIdentify || {}

  const attributes = {
    ...lodash.omit(parseExtra(extra), ['message']),
    appName: APP_NAME,
    queryString,
    fullPath,
    email,
    phoneNumber,
  }

  return attributes
}

const isDebugLoggerFeatureEnabled = () => {
  try {
    return abtProvider.isFeatureEnabled(DEBUG_LOGGER_FEATURE_FLAG)
  } catch (err) {
    return false
  }
}

const log = (message, extraAttributes = {}, level = LogLevel.INFO) => {
  try {
    if (level === LogLevel.DEBUG && !isDebugLoggerFeatureEnabled()) {
      return
    }

    const customAttributes = getDefaultAttributes(window, extraAttributes)

    // If NewRelic is not initialized, we will cache the event in the data layer
    if (!clientLogger) {
      window.loggerDataLayer?.push({
        event: message,
        level,
        data: customAttributes,
      })

      return
    }

    clientLogger.log(message, {
      level,
      customAttributes,
    })
  } catch (error) {
    console.warn({ error }, 'NewRelic log function is not available')
  }
}

const makeLoggerMethod = (level) => (message, extraAttributes = {}) =>
  log(message, extraAttributes, level)

const noticeError = (error, extraAttributes = {}) => {
  try {
    if (clientLogger) {
      const customAttributes = getDefaultAttributes(window, extraAttributes)
      clientLogger.noticeError(error, customAttributes)
    }
  } catch (error) {
    console.warn({ error }, 'NewRelic noticeError function is not available')
  }
}

const flushDataLayer = () => {
  if (window.loggerDataLayer) {
    while (window.loggerDataLayer.length > 0) {
      const { event, level, data } = window.loggerDataLayer.shift()

      log(event, data, level)
    }
  }
}

const initGlobalTrackingEvents = () => {
  window.addEventListener('online', () => {
    log(LOGGING_EVENTS.CONNECTION_RESTORED)
  })
  window.addEventListener('beforeunload', () => {
    log(LOGGING_EVENTS.LEAVING)
  })
}

const initializeNewRelic = ({ version }) => {
  if (window.newrelic) {
    window.newrelic.setApplicationVersion(version)

    clientLogger = window.newrelic
    setDefaultAttributes(window)
    initGlobalTrackingEvents()
  }
}

export const initializeLogger = ({ version } = {}) => {
  try {
    if (window.newrelic) {
      initializeNewRelic({ version })
    } else {
      window.addEventListener('newrelic', (evt) => {
        if (evt.detail.loaded) {
          initializeNewRelic()

          flushDataLayer()
        }
      })
    }
  } catch (error) {
    console.warn({ error }, 'Newrelic initialization failed')
  }
}

export default {
  log,
  noticeError,
  trace: makeLoggerMethod(LogLevel.TRACE),
  debug: makeLoggerMethod(LogLevel.DEBUG),
  error: makeLoggerMethod(LogLevel.ERROR),
  warn: makeLoggerMethod(LogLevel.WARN),
  identify,
}
